import {
  Box,
  Button,
  Card,
  CardActionArea,
  Stack,
  Typography,
  Skeleton,
  alpha,
} from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import HeadphonesIcon from '@mui/icons-material/Headphones'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { statsApi, type PopularSurah } from '../lib/api'
import { getSurahInfo, getLocalizedSurahName } from '../lib/surahs'
import { useOnlineStatus } from '../hooks/useOnlineStatus'

// Single in-flight promise shared across mounts. Both PopularSurahsSection
// and the sibling PopularAyahsSection use the same combined endpoint, so
// caching the response avoids a double round-trip when both sections mount
// together on the home page. Cleared once it resolves to a result; the
// individual sections own subsequent re-fetches on cancel/remount.
let inflight: Promise<Awaited<ReturnType<typeof statsApi.getPopular>>> | null = null
function fetchPopular(surahLimit: number, ayahLimit: number) {
  if (inflight) return inflight
  inflight = statsApi.getPopular(surahLimit, ayahLimit).finally(() => {
    inflight = null
  })
  return inflight
}
export { fetchPopular as _fetchPopularShared }

interface PopularSurahsSectionProps {
  /** How many to fetch (and render). Defaults to 10. */
  limit?: number
}

/**
 * Discover — top-N most-played surahs. Featured #1 paper card + compact
 * grid for the rest. Click → /reader/<n> (no auto-play).
 */
export function PopularSurahsSection({ limit = 10 }: PopularSurahsSectionProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [items, setItems] = useState<PopularSurah[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const online = useOnlineStatus()

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(false)
    fetchPopular(limit, limit)
      .then((resp) => {
        if (cancelled) return
        setItems(resp.surahs ?? [])
      })
      .catch((err) => {
        if (cancelled) return
        if (import.meta.env.DEV) console.warn('PopularSurahsSection: fetch failed', err)
        setError(true)
        setItems([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [limit])

  if (error && !online) return null // offline: hide the section instead of an error card

  const hasItems = !!items && items.length > 0
  const featured = hasItems ? items![0] : null
  const rest = hasItems ? items!.slice(1) : []

  const openSurah = (n: number) => navigate(`/reader/${n}`)

  return (
    <Box sx={{ mb: 3.5 }}>
      <Stack spacing={0.35} sx={{ mb: 1.75 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <TrendingUpIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            {t('discover.popularSurahs')}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ pl: { xs: 0, sm: 4 } }}>
          {t('discover.popularHint')}
        </Typography>
      </Stack>

      {loading ? (
        <Stack spacing={1.25}>
          <Skeleton variant="rounded" sx={{ height: 120, borderRadius: 2.5 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: 1.25,
            }}
          >
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" sx={{ height: 88, borderRadius: 2.5 }} />
            ))}
          </Box>
        </Stack>
      ) : !hasItems ? (
        <Box
          sx={{
            borderRadius: 2.5,
            border: 1,
            borderColor: 'divider',
            borderStyle: 'dashed',
            bgcolor: 'background.paper',
            p: 2.5,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {error ? t('discover.popularError') : t('discover.popularEmpty')}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.25}>
          {featured && (
            <FeaturedSurahCard
              item={featured}
              rank={1}
              onOpen={() => openSurah(featured.surahNumber)}
              name={
                getSurahInfo(featured.surahNumber)?.name
                ?? t('playlists.surahFallback', { number: featured.surahNumber })
              }
              meaning={getLocalizedSurahName(featured.surahNumber, i18n.language)}
              playsLabel={t('discover.plays', { count: featured.count })}
              openLabel={t('discover.open')}
              ariaLabel={t('discover.playSurah', {
                number: featured.surahNumber,
                name: getSurahInfo(featured.surahNumber)?.englishName ?? '',
              })}
            />
          )}

          {rest.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                gap: 1.25,
              }}
            >
              {rest.map((item, idx) => {
                const info = getSurahInfo(item.surahNumber)
                const rank = idx + 2
                return (
                  <GridSurahCard
                    key={item.surahNumber}
                    rank={rank}
                    name={`${item.surahNumber}. ${info?.name ?? t('playlists.surahFallback', { number: item.surahNumber })}`}
                    meaning={getLocalizedSurahName(item.surahNumber, i18n.language)}
                    playsLabel={t('discover.plays', { count: item.count })}
                    ariaLabel={t('discover.playSurah', {
                      number: item.surahNumber,
                      name: info?.englishName ?? '',
                    })}
                    onOpen={() => openSurah(item.surahNumber)}
                  />
                )
              })}
            </Box>
          )}
        </Stack>
      )}
    </Box>
  )
}

interface FeaturedSurahCardProps {
  item: PopularSurah
  rank: number
  name: string
  meaning: string
  playsLabel: string
  openLabel: string
  ariaLabel: string
  onOpen: () => void
}

function FeaturedSurahCard({
  rank,
  name,
  meaning,
  playsLabel,
  openLabel,
  ariaLabel,
  onOpen,
}: FeaturedSurahCardProps) {
  return (
    <Card
      sx={{
        overflow: 'hidden',
        borderColor: (th) => alpha(th.palette.primary.main, 0.22),
      }}
    >
      <Box sx={{ display: 'flex', minHeight: 120 }}>
        <Box
          aria-hidden
          sx={{
            width: 5,
            flexShrink: 0,
            bgcolor: 'primary.main',
          }}
        />
        <Box
          sx={{
            flex: 1,
            p: { xs: 2, sm: 2.25 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            gap: 2,
            background: (th) =>
              `linear-gradient(135deg, ${alpha(th.palette.primary.main, th.palette.mode === 'dark' ? 0.14 : 0.06)} 0%, transparent 70%)`,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 800,
              fontSize: 14,
            }}
            aria-hidden
          >
            #{rank}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', lineHeight: 1.25 }}>
              {name}
            </Typography>
            {meaning && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.25 }}>
                {meaning}
              </Typography>
            )}
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.75, color: 'text.secondary' }}>
              <HeadphonesIcon sx={{ fontSize: 15 }} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {playsLabel}
              </Typography>
            </Stack>
          </Box>
          <Button
            variant="contained"
            color="primary"
            endIcon={<PlayArrowIcon />}
            onClick={onOpen}
            aria-label={ariaLabel}
            sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, fontWeight: 700, flexShrink: 0 }}
          >
            {openLabel}
          </Button>
        </Box>
      </Box>
    </Card>
  )
}

interface GridSurahCardProps {
  rank: number
  name: string
  meaning: string
  playsLabel: string
  ariaLabel: string
  onOpen: () => void
}

function GridSurahCard({ rank, name, meaning, playsLabel, ariaLabel, onOpen }: GridSurahCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea
        onClick={onOpen}
        aria-label={ariaLabel}
        sx={{
          height: '100%',
          p: 1.75,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
          justifyContent: 'flex-start',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: 'primary.main',
            fontSize: '0.85rem',
            lineHeight: 1.4,
            minWidth: 28,
          }}
        >
          #{rank}
        </Typography>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3 }} noWrap>
            {name}
          </Typography>
          {meaning && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }} noWrap>
              {meaning}
            </Typography>
          )}
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.75, color: 'text.secondary' }}>
            <HeadphonesIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {playsLabel}
            </Typography>
          </Stack>
        </Box>
      </CardActionArea>
    </Card>
  )
}
